import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { SignalAccountStatus } from '@/types/signal';
import { CTraderSignalStatusBadge } from './CTraderSignalStatusBadge';
import { formatDateTime, truncateId } from '@/lib/utils';

interface CTraderSignalDetailModalProps {
  signal: SignalAccountStatus | null;
  open: boolean;
  onClose: () => void;
}

export const CTraderSignalDetailModal = ({ 
  signal, 
  open, 
  onClose 
}: CTraderSignalDetailModalProps) => {
  if (!signal) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            Signal Details
            <CTraderSignalStatusBadge status={signal.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Signal ID</span>
                <p className="font-mono text-sm">{truncateId(signal.id)}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Order ID</span>
                <p className="font-mono text-sm">{signal.order_id || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Symbol</span>
                <p className="font-semibold">{signal.symbol || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Action</span>
                <p>{signal.action || 'N/A'}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Account Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Account Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Account ID</span>
                <p className="font-mono text-sm">{signal.accountid_trading}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Trading Account ID</span>
                <p className="font-mono text-sm">{truncateId(signal.trading_account_id)}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">API Connection ID</span>
                <p className="font-mono text-sm">{truncateId(signal.api_connection_id)}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Volume Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Volume Information</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Original Volume</span>
                <p className="font-semibold">{signal.original_volume?.toLocaleString()}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Volume Sent</span>
                <p className="font-semibold">{signal.volume_sent?.toLocaleString()}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Volume Multiplier</span>
                <p className="font-semibold">{signal.volume_multiplier}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Risk Management */}
          {(signal.stop_loss || signal.take_profit || signal.relative_stop_loss || signal.relative_take_profit) && (
            <>
              <div>
                <h3 className="text-lg font-semibold mb-3">Risk Management</h3>
                <div className="grid grid-cols-2 gap-4">
                  {signal.stop_loss && (
                    <div>
                      <span className="text-sm text-muted-foreground">Stop Loss</span>
                      <p className="font-semibold">{signal.stop_loss}</p>
                    </div>
                  )}
                  {signal.take_profit && (
                    <div>
                      <span className="text-sm text-muted-foreground">Take Profit</span>
                      <p className="font-semibold">{signal.take_profit}</p>
                    </div>
                  )}
                  {signal.relative_stop_loss && (
                    <div>
                      <span className="text-sm text-muted-foreground">Relative Stop Loss</span>
                      <p className="font-semibold">{signal.relative_stop_loss}</p>
                    </div>
                  )}
                  {signal.relative_take_profit && (
                    <div>
                      <span className="text-sm text-muted-foreground">Relative Take Profit</span>
                      <p className="font-semibold">{signal.relative_take_profit}</p>
                    </div>
                  )}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Timing Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Timing</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Response Time</span>
                <p>{signal.response_time ? formatDateTime(signal.response_time) : 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Error Information */}
          {signal.error_message && (
            <>
              <Separator />
              <div>
                <h3 className="text-lg font-semibold mb-3 text-red-500">Error Details</h3>
                <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-red-700 dark:text-red-300 font-mono text-sm">
                    {signal.error_message}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};